import type { ViewBackedAppSourceWireV1 } from '../view-backed-app/wire-contract';
import { SourceContractUnavailableError } from '../view-backed-app/wire-contract';
import type { CalendarSource, CalendarSourceOwner } from './types';

export function decodeCalendarSourceWireV1(value: unknown): CalendarSource {
  const row = value as ViewBackedAppSourceWireV1;
  const area = row.area;
  const privateScope = row.scope === 'private';
  const privateArea = area?.kind === 'private';
  if (privateScope !== privateArea)
    unavailable('Calendar source scope and area contradict');
  const owner = decodeOwner(row.presentation);
  if (privateScope) {
    if (
      !area ||
      !canonicalNonblank(area.ownerUserId) ||
      area.workspaceId !== row.workspaceId
    ) {
      unavailable('Invalid private calendar area identity');
    }
    const validOwner = owner !== null && row.presentationError === null;
    const unavailableOwner =
      owner === null && row.presentationError === 'owner_profile_unavailable';
    if (!validOwner && !unavailableOwner)
      unavailable('Invalid private calendar presentation');
    if (owner && owner.userId !== area.ownerUserId)
      unavailable('Calendar owner and area identity contradict');
  } else if (owner !== null || row.presentationError !== null) {
    unavailable('Shared calendar source cannot expose an owner presentation');
  }
  const {
    createdAt: _createdAt,
    diagnostics,
    presentation,
    sortOrder: _sortOrder,
    updatedAt: _updatedAt,
    workspaceId: _workspaceId,
    ...source
  } = row;
  return {
    ...source,
    columns: decodeColumns(diagnostics),
    owner,
  } as CalendarSource;
}

function decodeOwner(value: unknown): CalendarSourceOwner | null {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['owner', 'version']) ||
    value.version !== 1
  )
    unavailable('Invalid calendar presentation');
  const owner = value.owner;
  if (
    !isRecord(owner) ||
    !hasExactKeys(owner, ['avatarUrl', 'displayName', 'userId'])
  )
    unavailable('Invalid calendar owner');
  if (
    !canonicalNonblank(owner.userId) ||
    !canonicalNonblank(owner.displayName) ||
    (owner.avatarUrl !== null && !safeAvatarUrl(owner.avatarUrl))
  )
    unavailable('Invalid calendar owner fields');
  return owner as unknown as CalendarSourceOwner;
}

function canonicalNonblank(value: unknown): value is string {
  return typeof value === 'string' && !!value && value.trim() === value;
}

function safeAvatarUrl(value: unknown): value is string {
  if (!canonicalNonblank(value)) return false;
  try {
    const parsed = new URL(value, 'https://streamlain.invalid');
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function decodeColumns(value: Record<string, unknown> | null) {
  const columns = value?.columns;
  return isRecord(columns) ? columns : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]) {
  const actual = Object.keys(value).sort();
  return (
    actual.length === keys.length &&
    [...keys].sort().every((key, index) => actual[index] === key)
  );
}
function unavailable(message: string): never {
  throw new SourceContractUnavailableError(message);
}
